import React from 'react';
import {ReactMarkdownProps} from 'react-markdown/lib/ast-to-react';
import cx from 'classnames';
import NextLink from 'next/link';

import {ActionPlanImpactLevel} from '#apollo/schema';
import {Button, Icon, Text} from '#component/base';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {Markdown} from '#component/partial/Markdown';
import {Paragraph} from '#component/partial/Markdown/component/Paragraph';
import {sprinkles} from '#style/sprinkles';

import {MAX_TIPS_CHAR_LENGTH, shortenTips} from './GoalsBody.util';

import * as css from './GoalsBody.css';

export interface GoalsBodyProps {
  surveySubmissionId?: string;
  healthFactorAssessment?: ActionPlanPage.HealthFactorAssessment | null;
  tips?: string;
  benefits?: string;
  impactLevel?: ActionPlanImpactLevel | null;
  variant?: 'default' | 'drawer';
}
function GoalsBody({
  surveySubmissionId,
  healthFactorAssessment,
  impactLevel,
  tips,
  benefits,
  variant = 'default',
}: GoalsBodyProps) {
  const {healthFactor, categoryAssessment} = healthFactorAssessment || {};

  const [tipsExpanded, setTipsExpanded] = React.useState<boolean>(false);
  const [tipsText, setTipsText] = React.useState<string>(shortenTips(tips));

  const toggleExpandTips = React.useCallback(() => {
    if (tipsExpanded) {
      setTipsText(shortenTips(tips));
      setTipsExpanded(false);
    } else {
      setTipsText(tips || '');
      setTipsExpanded(true);
    }
  }, [tips, tipsExpanded]);

  return (
    <div className={css.contentWrapper({variant})}>
      <div
        className={sprinkles({
          colStart: 1,
          colSpan: [4, 6],
        })}>
        <Text
          as="h4"
          variant="label"
          color="deepBurgundy"
          className={sprinkles({
            display: 'flex',
            items: 'center',
            gap: 12,
            mB: 12,
          })}>
          <Icon name="Heartbeat" color="deepBurgundy" />
          Why this is important for you
        </Text>
        <Text variant="body" color="mineShaft" className={css.bodyMedium}>
          <GoalsTexts contents={benefits || ''} />
        </Text>
        {impactLevel && (
          <>
            <Text
              as="p"
              variant="body"
              className={sprinkles({
                mT: 24,
              })}>
              Based on your unique DNA methylation, we believe this goal is of:
            </Text>
            <Text as="p" variant="body" fontWeight="semiBold" color="mineShaft">
              {impactLevel} impact to you
            </Text>
          </>
        )}
        {variant === 'default' && (
          <NextLink
            href={`/surveySubmission/${surveySubmissionId}/${categoryAssessment?.category?.id}/${healthFactor?.id}`}>
            <Button
              variant="secondary"
              className={sprinkles({
                display: ['none', 'none', 'flex'],
                w: 'maxContent',
                mT: 32,
              })}>
              Why it works
            </Button>
          </NextLink>
        )}
      </div>
      <div
        className={sprinkles({
          colStart: [1, 1, 8],
          colSpan: [4, 8, 5],
          mT: [40, 0],
        })}>
        <Text
          as="h4"
          variant="label"
          color="deepBurgundy"
          className={sprinkles({
            display: 'flex',
            items: 'center',
            mB: 12,
            gap: 12,
            mT: [24, 24, 0],
          })}>
          <Icon name="PushPin" color="deepBurgundy" />
          How to get started
        </Text>
        <GoalsTexts contents={tipsText || ''} />
        {(tips || '').length > MAX_TIPS_CHAR_LENGTH && (
          <Button className={sprinkles({mT: 4})} variant="secondary" onClick={toggleExpandTips}>
            {tipsExpanded ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </div>
      <NextLink
        href={`/surveySubmission/${surveySubmissionId}/${categoryAssessment?.category?.id}/${healthFactor?.id}`}>
        <Button
          variant="secondary"
          className={sprinkles({
            display: ['flex', 'flex', 'none'],
            w: ['full', 'maxContent'],
            mT: [16, 16, 32],
          })}>
          Why it works
        </Button>
      </NextLink>
    </div>
  );
}

export {GoalsBody};

const GoalsTexts = ({contents}: {contents: string}) => {
  const lastLiIndex = (contents.match(/\n-/g) || []).length;
  return (
    <Markdown
      className={sprinkles({flex: 'flex', dir: 'col', gap: 16})}
      components={{
        p: (props: ReactMarkdownProps) => <Paragraph {...props} />,
        li: (props: ReactMarkdownProps) => (
          <li className={cx(css.tipsListItem, props.index === lastLiIndex && css.lastTipsListItem)}>
            <Text as="span" className={css.textSmallSize} {...props} />
          </li>
        ),
        ul: (props: ReactMarkdownProps) => <Text as="ul" {...props} />,
      }}
      data={contents || ''}
    />
  );
};
